/**
 * AuditLog viewer page — /audit-log (tenant-side).
 *
 * Layout:
 *   - Filter bar: date range, severity, status, action prefix, free-text search
 *   - Table: createdAt · action · actor · target · severity · status
 *   - Click row → drawer with full details + before/after diff
 *   - Pagination footer
 *   - Export CSV button (top-right)
 *
 * Visibility:
 *   tenant_owner, super_admin, business_admin only.
 *   business_admin sees their business + tenant-wide entries (enforced
 *   server-side in the service's readScope()).
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Shield,
  Search,
  CalendarDays,
  Download,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle2,
  XCircle,
  Ban,
  Info,
  AlertTriangle,
  ShieldAlert,
  User as UserIcon,
  Cpu,
  Webhook,
  Filter as FilterIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuditLogSearch, useAuditLogEntry } from "@/hooks/useAuditLog";
import { auditLogService } from "@/services/auditLogService";
import {
  type AuditLogEntry,
  type AuditLogSearchFilters,
  type AuditSeverity,
  type AuditStatus,
  type AuditActorType,
  actionLabel,
  SEVERITY_LABELS,
  STATUS_LABELS,
  ACTOR_TYPE_LABELS,
} from "@/types/auditLog.types";

const DEFAULT_LIMIT = 50;

// ═══════════════════════════════════════════════════════════════════
// Page
// ═══════════════════════════════════════════════════════════════════

const AuditLogPage: React.FC = () => {
  const [filters, setFilters] = useState<AuditLogSearchFilters>({
    page: 1,
    limit: DEFAULT_LIMIT,
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError, isFetching } = useAuditLogSearch(filters);

  // Reset to page 1 whenever a non-pagination filter changes
  const updateFilter = (patch: Partial<AuditLogSearchFilters>) => {
    setFilters((prev) => {
      // If anything other than page/limit changed, reset to page 1
      const onlyPagination = Object.keys(patch).every(
        (k) => k === "page" || k === "limit",
      );
      return {
        ...prev,
        ...patch,
        page: onlyPagination ? (patch.page ?? prev.page) : 1,
      };
    });
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: DEFAULT_LIMIT });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Export uses current filters but no pagination
      const { page, limit, ...rest } = filters;
      void page;
      void limit;
      await auditLogService.downloadCsv(rest);
      toast.success("Export downloaded");
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e.message || "Couldn't export audit log");
    } finally {
      setExporting(false);
    }
  };

  const hasFilters = useMemo(() => {
    return Object.entries(filters).some(
      ([k, v]) => k !== "page" && k !== "limit" && v !== undefined && v !== "",
    );
  }, [filters]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-4">
      {/* ─── Header ─────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            <Shield className="h-3 w-3" />
            Compliance trail
          </div>
          <h1 className="text-2xl font-semibold">Audit log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Append-only record of every sensitive action. Filter, search, and
            export for compliance reviews.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={exporting || isLoading}
          className="gap-2"
        >
          {exporting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          Export CSV
        </Button>
      </div>

      {/* ─── Filter bar ────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <FilterIcon className="h-3.5 w-3.5" />
            Filters
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="ml-auto h-6 px-2 text-xs"
              >
                <X className="h-3 w-3 mr-1" /> Clear
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="al-from" className="text-xs">
                From
              </Label>
              <div className="relative">
                <CalendarDays className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input
                  id="al-from"
                  type="date"
                  value={filters.from || ""}
                  onChange={(e) => updateFilter({ from: e.target.value })}
                  className="pl-8 h-9"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="al-to" className="text-xs">
                To
              </Label>
              <div className="relative">
                <CalendarDays className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input
                  id="al-to"
                  type="date"
                  value={filters.to || ""}
                  onChange={(e) => updateFilter({ to: e.target.value })}
                  className="pl-8 h-9"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="al-severity" className="text-xs">
                Severity
              </Label>
              <Select
                value={filters.severity || "any"}
                onValueChange={(v) =>
                  updateFilter({
                    severity: v === "any" ? undefined : (v as AuditSeverity),
                  })
                }
              >
                <SelectTrigger id="al-severity" className="h-9">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any severity</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="al-status" className="text-xs">
                Status
              </Label>
              <Select
                value={filters.status || "any"}
                onValueChange={(v) =>
                  updateFilter({
                    status: v === "any" ? undefined : (v as AuditStatus),
                  })
                }
              >
                <SelectTrigger id="al-status" className="h-9">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any status</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failure">Failure</SelectItem>
                  <SelectItem value="denied">Denied</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="al-action" className="text-xs">
                Action (exact, or prefix ending in “.”)
              </Label>
              <Input
                id="al-action"
                placeholder="e.g. user.role_changed  or  auth."
                value={filters.action || ""}
                onChange={(e) =>
                  updateFilter({ action: e.target.value || undefined })
                }
                className="h-9"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="al-q" className="text-xs">
                Search (actor, target, action)
              </Label>
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input
                  id="al-q"
                  placeholder="Email, name, target label…"
                  value={filters.q || ""}
                  onChange={(e) =>
                    updateFilter({ q: e.target.value || undefined })
                  }
                  className="pl-8 h-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ─── Results ────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-0">
          {isLoading && !data ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" />
              Loading audit log…
            </div>
          ) : isError ? (
            <div className="py-10 text-center">
              <AlertCircle className="h-5 w-5 text-rose-600 mx-auto mb-2" />
              <p className="text-sm text-rose-700">
                Couldn't load the audit log.
              </p>
            </div>
          ) : !data || data.rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <Shield className="h-6 w-6 mx-auto mb-2 opacity-40" />
              <p className="font-medium">
                No audit entries match your filters.
              </p>
              {hasFilters && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={clearFilters}
                  className="mt-2"
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">When</th>
                      <th className="text-left px-3 py-2 font-medium">
                        Action
                      </th>
                      <th className="text-left px-3 py-2 font-medium">Actor</th>
                      <th className="text-left px-3 py-2 font-medium">
                        Target
                      </th>
                      <th className="text-left px-3 py-2 font-medium">
                        Severity
                      </th>
                      <th className="text-left px-3 py-2 font-medium">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.rows.map((row) => (
                      <tr
                        key={row._id}
                        onClick={() => setOpenId(row._id)}
                        className="hover:bg-muted/40 cursor-pointer transition-colors"
                      >
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-muted-foreground tabular-nums">
                          {formatWhen(row.createdAt)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-sm">
                            {actionLabel(row.action)}
                          </div>
                          <code className="text-[10px] text-muted-foreground">
                            {row.action}
                          </code>
                        </td>
                        <td className="px-3 py-2">
                          <ActorCell
                            actorType={row.actorType}
                            email={row.actorEmail}
                            name={row.actorName}
                            role={row.actorRole}
                          />
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {row.targetLabel ? (
                            <span className="truncate block max-w-[18ch]">
                              {row.targetLabel}
                            </span>
                          ) : row.targetType ? (
                            <span className="text-muted-foreground">
                              {row.targetType}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <SeverityBadge severity={row.severity} />
                        </td>
                        <td className="px-3 py-2">
                          <StatusBadge status={row.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination footer */}
              <div className="flex items-center justify-between gap-3 px-3 py-2 border-t bg-muted/20 text-xs">
                <div className="text-muted-foreground">
                  {data.total === 0
                    ? "No entries"
                    : `Showing ${(data.page - 1) * data.limit + 1}–${Math.min(data.page * data.limit, data.total)} of ${data.total}`}
                  {isFetching && (
                    <Loader2 className="inline h-3 w-3 ml-2 animate-spin" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      updateFilter({
                        page: Math.max(1, (filters.page || 1) - 1),
                      })
                    }
                    disabled={data.page <= 1 || isFetching}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <span className="tabular-nums px-2">
                    {data.page} / {data.totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      updateFilter({ page: (filters.page || 1) + 1 })
                    }
                    disabled={data.page >= data.totalPages || isFetching}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ─── Detail drawer ──────────────────────────────────── */}
      <AuditDetailDrawer id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Detail drawer
// ═══════════════════════════════════════════════════════════════════

const AuditDetailDrawer: React.FC<{
  id: string | null;
  onClose: () => void;
}> = ({ id, onClose }) => {
  const { data: entry, isLoading } = useAuditLogEntry(id);

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-[#4CAF50]" />
            Audit entry
          </SheetTitle>
          <SheetDescription>
            {entry ? formatWhen(entry.createdAt, true) : "Loading…"}
          </SheetDescription>
        </SheetHeader>

        {isLoading || !entry ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" />
            Loading…
          </div>
        ) : (
          <DetailBody entry={entry} />
        )}
      </SheetContent>
    </Sheet>
  );
};

const DetailBody: React.FC<{ entry: AuditLogEntry }> = ({ entry }) => (
  <div className="mt-4 space-y-5 text-sm">
    {/* Action header */}
    <div className="rounded-md border bg-card p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
        Action
      </div>
      <div className="font-semibold">{actionLabel(entry.action)}</div>
      <code className="text-[10px] text-muted-foreground">{entry.action}</code>
      <div className="flex items-center gap-2 mt-2">
        <SeverityBadge severity={entry.severity} />
        <StatusBadge status={entry.status} />
      </div>
    </div>

    {/* Actor + target */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <FieldBlock label="Actor">
        <ActorCell
          actorType={entry.actorType}
          email={entry.actorEmail}
          name={entry.actorName}
          role={entry.actorRole}
        />
      </FieldBlock>
      <FieldBlock label="Target">
        {entry.targetLabel ? (
          <div>
            <div className="font-medium">{entry.targetLabel}</div>
            {entry.targetType && (
              <div className="text-xs text-muted-foreground">
                {entry.targetType}
              </div>
            )}
          </div>
        ) : entry.targetType ? (
          <div className="text-muted-foreground">{entry.targetType}</div>
        ) : (
          <div className="text-muted-foreground">—</div>
        )}
      </FieldBlock>
    </div>

    {/* Changes diff */}
    {entry.changes && entry.changes.length > 0 && (
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
          Changes ({entry.changes.length})
        </div>
        <div className="rounded-md border bg-card divide-y">
          {entry.changes.map((c, i) => (
            <div key={i} className="px-3 py-2 text-xs">
              <div className="font-medium font-mono mb-1">{c.field}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase">
                    Before
                  </div>
                  <DiffValue value={c.before} tone="rose" />
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase">
                    After
                  </div>
                  <DiffValue value={c.after} tone="green" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Error */}
    {(entry.errorCode || entry.errorMessage) && (
      <FieldBlock label="Error" tone="rose">
        {entry.errorCode && (
          <div className="text-xs font-mono">{entry.errorCode}</div>
        )}
        {entry.errorMessage && (
          <div className="text-xs mt-1">{entry.errorMessage}</div>
        )}
      </FieldBlock>
    )}

    {/* Request context */}
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
        Request context
      </div>
      <dl className="rounded-md border bg-card divide-y text-xs">
        <ContextRow label="Method" value={entry.method} />
        <ContextRow label="Path" value={entry.path} monospace />
        <ContextRow label="IP" value={entry.ip} monospace />
        <ContextRow label="User agent" value={entry.userAgent} />
        <ContextRow label="Request ID" value={entry.requestId} monospace />
        <ContextRow
          label="Business ID"
          value={entry.businessId || "tenant-wide"}
          monospace
        />
        <ContextRow label="Entry ID" value={entry._id} monospace />
      </dl>
    </div>

    {/* Metadata */}
    {entry.metadata != null && (
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
          Metadata
        </div>
        <pre className="rounded-md border bg-muted/40 p-3 text-[10px] font-mono whitespace-pre-wrap break-words max-h-60 overflow-y-auto">
          {JSON.stringify(entry.metadata, null, 2)}
        </pre>
      </div>
    )}
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// Helpers + UI primitives
// ═══════════════════════════════════════════════════════════════════

const FieldBlock: React.FC<{
  label: string;
  tone?: "rose";
  children: React.ReactNode;
}> = ({ label, tone, children }) => (
  <div
    className={`rounded-md border p-3 ${tone === "rose" ? "bg-rose-50 border-rose-200" : "bg-card"}`}
  >
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
      {label}
    </div>
    {children}
  </div>
);

const ContextRow: React.FC<{
  label: string;
  value: string | null;
  monospace?: boolean;
}> = ({ label, value, monospace }) => (
  <div className="flex items-center gap-2 px-3 py-1.5">
    <dt className="text-muted-foreground w-28 shrink-0">{label}</dt>
    <dd
      className={`flex-1 truncate ${monospace ? "font-mono text-[10px]" : ""}`}
    >
      {value || <span className="text-muted-foreground/60">—</span>}
    </dd>
  </div>
);

const DiffValue: React.FC<{ value: unknown; tone: "rose" | "green" }> = ({
  value,
  tone,
}) => {
  const display =
    value === null || value === undefined
      ? "—"
      : typeof value === "object"
        ? JSON.stringify(value)
        : String(value);
  return (
    <div
      className={`mt-0.5 rounded px-2 py-1 font-mono text-[10px] break-words ${
        tone === "rose"
          ? "bg-rose-50 text-rose-900 border border-rose-200"
          : "bg-emerald-50 text-emerald-900 border border-emerald-200"
      }`}
    >
      {display.length > 200 ? display.slice(0, 200) + "…" : display}
    </div>
  );
};

const ActorCell: React.FC<{
  actorType: AuditActorType;
  email: string | null;
  name: string | null;
  role: string | null;
}> = ({ actorType, email, name, role }) => {
  const Icon =
    actorType === "operator"
      ? ShieldAlert
      : actorType === "system"
        ? Cpu
        : actorType === "webhook"
          ? Webhook
          : UserIcon;
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-sm truncate">{name || email || "—"}</div>
        <div className="text-[10px] text-muted-foreground truncate">
          {role || ACTOR_TYPE_LABELS[actorType]}
        </div>
      </div>
    </div>
  );
};

const SeverityBadge: React.FC<{ severity: AuditSeverity }> = ({ severity }) => {
  const cfg = {
    critical: {
      Icon: ShieldAlert,
      cls: "bg-rose-100 text-rose-700 border-rose-200",
    },
    warning: {
      Icon: AlertTriangle,
      cls: "bg-amber-100 text-amber-800 border-amber-200",
    },
    info: { Icon: Info, cls: "bg-slate-100 text-slate-600 border-slate-200" },
  }[severity];
  return (
    <Badge
      variant="outline"
      className={`gap-1 text-[10px] font-semibold ${cfg.cls}`}
    >
      <cfg.Icon className="h-2.5 w-2.5" />
      {SEVERITY_LABELS[severity]}
    </Badge>
  );
};

const StatusBadge: React.FC<{ status: AuditStatus }> = ({ status }) => {
  const cfg = {
    success: {
      Icon: CheckCircle2,
      cls: "bg-emerald-100 text-emerald-700 border-emerald-200",
    },
    failure: {
      Icon: XCircle,
      cls: "bg-rose-100 text-rose-700 border-rose-200",
    },
    denied: { Icon: Ban, cls: "bg-amber-100 text-amber-800 border-amber-200" },
  }[status];
  return (
    <Badge
      variant="outline"
      className={`gap-1 text-[10px] font-semibold ${cfg.cls}`}
    >
      <cfg.Icon className="h-2.5 w-2.5" />
      {STATUS_LABELS[status]}
    </Badge>
  );
};

function formatWhen(iso: string, full = false): string {
  const d = new Date(iso);
  if (full) {
    return d.toLocaleString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }
  return d.toLocaleString("en-GB", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default AuditLogPage;
